import { useState, useEffect } from 'react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { fetchAdminSettings, updateSettings } from '@/lib/api'

const FIELDS: Array<{ key: string; label: string; type?: string; rows?: number }> = [
  { key: 'business_name',    label: 'Business Name' },
  { key: 'phone',            label: 'Phone' },
  { key: 'lead_email',       label: 'Lead Notification Email' },
  { key: 'website',          label: 'Website URL' },
  { key: 'brand_color',      label: 'Brand Color (hex)' },
  { key: 'quote_valid_days', label: 'Quote Valid Days' },
  { key: 'disclaimer_version', label: 'Disclaimer Version' },
  { key: 'pricing_disclaimer', label: 'Pricing Disclaimer', type: 'textarea', rows: 4 },
  { key: 'thank_you_message', label: 'Thank-You Message', type: 'textarea', rows: 3 },
]

export default function AdminSettings() {
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAdminSettings()
      .then(setValues)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const updated = await updateSettings(values)
      setValues(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <PageHeader title="Settings" subtitle="Business info, disclaimer, and appearance" />
        <div className="text-slate-400 text-sm">Loading…</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <PageHeader title="Settings" subtitle="Business info, disclaimer, and appearance" />

      <form onSubmit={handleSave} className="max-w-2xl space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          {FIELDS.map(({ key, label, type, rows }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
              {type === 'textarea' ? (
                <textarea
                  rows={rows ?? 3}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
                />
              ) : (
                <input
                  type="text"
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              )}
            </div>
          ))}
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
          {saved && <span className="text-green-600 text-sm font-medium">Saved!</span>}
        </div>
      </form>
    </AdminLayout>
  )
}
